import { Injectable } from '@angular/core';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { FinanceDisbursementRequestAssignmentData } from '../models/disbursement/disbursement-request-assignment-data.model';
import { DisbursementRequestModel } from '../models/disbursement/disbursement-request.model';

@Injectable()
export class DisbursementService extends BaseApiService {
    /**
     * Gets the finance disbursement request.
     * @param id The finance disbursement request id
     */
    public getDisbursementRequest(id: number) {
        return this.get(`Finance/Disbursement/${id}`);
    }

    /**
     * Searches general ledgers for the provided search criteria
     * @param criteria The search criteria
     */
    public searchDisbursements(criteria) {
        return this.post('Finance/Disbursement/Search', criteria);
    }

    /**
     *  Updates the finance disbursement request
     * @param model The update disbursement request model
     */
    public updateDisbursement(request) {
        return this.post('Finance/Disbursement/' + request.financeDisbursementRequestId, request);
    }

    /**
     * Saves the disbursement request
     * @param request The disbursement request
     */
    public saveDisbursementRequest(request) {
        return this.post('Finance/Disbursement/SaveDisbursementRequest', request);
    }

    /**
     * Gets authorized finance disbursement request for the given date.
     * @param date The date of the disbursement request
     */
    public searchAuthorizedDisbursements(date: string) {
        return this.get(`Finance/Disbursement/Authorized/${date}`);
    }

    /**
     * Gets the disbursement fuel tax request products based on the products that are mapped to the provided disbursement request type.
     * @param financeDisbursementRequestTypeId The finance disbursement request type id
     */
    public getNewFinanceDisbursementFuelTaxRequestProducts(financeDisbursementRequestTypeId: number) {
        return this.get(`Finance/Disbursement/FuelTax/Products/${financeDisbursementRequestTypeId}`);
    }

    /**
     * Gets all users in the Fuel Tax Refund team, for the current business unit.
     */
    public getFinanceDisbursementFuelTaxRequestUsers() {
        return this.get('Finance/Disbursement/FuelTax/Users');
    }

    /**
     * Gets the current user's set of permissions for accessing fuel tax request functions on the provided request.
     * @param financeDisbursementRequestId The finance disbursement request id
     */
    public getFinanceDisbursementRequestUserAccess(financeDisbursementRequestId: number) {
        return this.get(`Finance/Disbursement/FuelTax/UserAccess/${financeDisbursementRequestId}`);
    }

    /**
     * Determines whether a save of the provided disbursment fuel tax request would result in creating a duplicate request, and if so, determines
     * whether Manager or Supervisor approval is required for the creation of the request.
     * @param request The disbursement fuel tax request
     */
    public isApprovalRequiredForDuplicateDisbursementFuelTaxRequest(request) {
        return this.post('Finance/Disbursement/FuelTax/CheckDuplicates', request);
    }

    /**
     * Saves the disbursement fuel tax request
     * @param request The disbursement fuel tax request
     */
    public saveDisbursementFuelTaxRequest(request) {
        return this.post('Finance/Disbursement/FuelTax/Save', request);
    }

    /**
     * Checks the disbursement fuel tax request to see if all documents have been uploaded, and if so, sets the status to Pending.
     * @param financeDisbursementRequestId The disbursement request id
     */
    public checkDisbursementFuelTaxRequestDocumentUploadAndUpdateStatus(financeDisbursementRequestId: number) {
        return this.post(`Finance/Disbursement/FuelTax/UploadComplete/${financeDisbursementRequestId}`, null);
    }

    /**
     * Denies the disbursement fuel tax request.
     * @param financeDisbursementRequest The disbursement request
     */
    public denyFinanceDisbursementFuelTaxRequest(financeDisbursementRequest: DisbursementRequestModel) {
        return this.post('Finance/Disbursement/FuelTax/Deny', financeDisbursementRequest);
    }

    /**
     * Processes the disbursement fuel tax request.
     * @param financeDisbursementRequestId The disbursement request id
     */
    public processFinanceDisbursementFuelTaxRequest(financeDisbursementRequestId: number) {
        return this.post(`Finance/Disbursement/FuelTax/Process/${financeDisbursementRequestId}`, null);
    }

    /**
     * Returns the disbursement fuel tax request to the processor.
     * @param financeDisbursementRequestId The disbursement request id
     */
    public returnFinanceDisbursementFuelTaxRequestToProcessor(financeDisbursementRequestId: number) {
        return this.post(`Finance/Disbursement/FuelTax/Return/${financeDisbursementRequestId}`, null);
    }

    /**
     * Approves the disbursement fuel tax request.
     * @param financeDisbursementRequestId The disbursement request id
     */
    public approveFinanceDisbursementFuelTaxRequest(financeDisbursementRequestId: number) {
        return this.post(`Finance/Disbursement/FuelTax/Approve/${financeDisbursementRequestId}`, null);
    }

    /**
     * Deletes the disbursement fuel tax request.
     * @param financeDisbursementRequestId The disbursement request id
     */
    public deleteFinanceDisbursementFuelTaxRequest(financeDisbursementRequestId: number) {
        return this.delete(`Finance/Disbursement/FuelTax/${financeDisbursementRequestId}`);
    }

    /**
     * Updates a list of disbursement requests with the assigned user.
     * @param financeDisbursementRequestAssignmentData The disbursement request update data
     */
    public assignDisbursementFuelTaxRequests(financeDisbursementRequestAssignmentData: FinanceDisbursementRequestAssignmentData) {
        return this.post('Finance/Disbursement/FuelTax/Assign', financeDisbursementRequestAssignmentData);
    }
}
